import {Component, EventEmitter, HostListener, Input, OnInit, Output} from '@angular/core';
import {CanaisVendaFacade} from '../../canais-venda/canais-venda.facade';
import {
  CanalVendaVM,
  PrecoOpcaoCanalVendaVM
} from '../../canais-venda/canais-venda.models';
import {NotificationService} from '../../services/notification.service';

interface PrecoOpcaoCanalEdicao extends PrecoOpcaoCanalVendaVM {
  modo: 'CONFIGURACAO_CANAL' | 'ESPECIFICO';
  valor: number | null;
  alterado: boolean;
  erro: string;
}

interface PrecosCanalAdicionalEdicao {
  canal: Pick<CanalVendaVM, 'id' | 'nome' | 'plataforma' | 'ativo' | 'percentualPrecoPadrao'>;
  opcoes: PrecoOpcaoCanalEdicao[];
  salvando: boolean;
}

@Component({
  selector: 'app-precos-canais-adicional',
  templateUrl: './precos-canais-adicional.component.html',
  styleUrls: ['./precos-canais-adicional.component.scss'],
  providers: [CanaisVendaFacade]
})
export class PrecosCanaisAdicionalComponent implements OnInit {
  @Input() adicionalId: number | null = null;
  @Input() opcaoId: number | null = null;
  @Input() opcaoNome = '';
  @Output() fechou = new EventEmitter<void>();

  canais: PrecosCanalAdicionalEdicao[] = [];
  carregando = false;
  erro = '';
  filtroOpcaoId: number | null = null;

  constructor(
    private canaisVenda: CanaisVendaFacade,
    private notificacao: NotificationService
  ) {}

  ngOnInit(): void {
    this.filtroOpcaoId = Number(this.opcaoId) || null;
    void this.carregue();
  }

  @HostListener('document:keydown.escape')
  fechePeloTeclado(): void {
    this.feche();
  }

  feche(): void {
    if(this.canais.some(canal => canal.salvando)) return;
    this.fechou.emit();
  }

  async carregue(): Promise<void> {
    if(!Number(this.adicionalId) || this.carregando) return;
    this.carregando = true;
    this.erro = '';
    try {
      const resposta = await this.canaisVenda.listeCanaisAdicional(Number(this.adicionalId));
      this.canais = resposta.canais.map(item => ({
        canal: item.canal,
        salvando: false,
        opcoes: item.opcoes.map(opcao => ({
          ...opcao,
          modo: opcao.origem === 'ESPECIFICO' ? 'ESPECIFICO' : 'CONFIGURACAO_CANAL',
          valor: opcao.precoFinal,
          alterado: false,
          erro: ''
        }))
      }));
    } catch(erro) {
      this.erro = erro instanceof Error && erro.message
        ? erro.message : 'Não foi possível carregar os preços por canal.';
    } finally {
      this.carregando = false;
    }
  }

  opcoesTabela(): PrecoOpcaoCanalEdicao[] {
    const opcoes = new Map<number, PrecoOpcaoCanalEdicao>();
    this.canais.forEach(canal => canal.opcoes.forEach(opcao => {
      if(!opcoes.has(Number(opcao.opcaoId))) opcoes.set(Number(opcao.opcaoId), opcao);
    }));
    const resultado = Array.from(opcoes.values());
    return this.filtroOpcaoId
      ? resultado.filter(opcao => Number(opcao.opcaoId) === this.filtroOpcaoId)
      : resultado;
  }

  opcaoNoCanal(canal: PrecosCanalAdicionalEdicao, opcaoId: number): PrecoOpcaoCanalEdicao | null {
    return canal.opcoes.find(opcao => Number(opcao.opcaoId) === Number(opcaoId)) || null;
  }

  removaFiltro(): void {
    this.filtroOpcaoId = null;
  }

  statusPreco(opcao: PrecoOpcaoCanalEdicao): string {
    if(opcao.alterado) return 'Alteração não salva';
    if(opcao.precoFinal == null) return 'Sem preço definido';
    return opcao.origem === 'ESPECIFICO'
      ? 'Preço específico salvo'
      : 'Calculado pela regra do canal';
  }

  marquePreco(canal: PrecosCanalAdicionalEdicao, opcao: PrecoOpcaoCanalEdicao): void {
    opcao.modo = this.podeUsarRegraDoCanal(canal) && this.precoInformadoSegueRegra(canal, opcao)
      ? 'CONFIGURACAO_CANAL' : 'ESPECIFICO';
    opcao.alterado = true;
    opcao.erro = '';
  }

  useConfiguracaoCanal(canal: PrecosCanalAdicionalEdicao, opcao: PrecoOpcaoCanalEdicao): void {
    if(!this.podeUsarRegraDoCanal(canal)) return;
    opcao.modo = 'CONFIGURACAO_CANAL';
    opcao.valor = this.precoPelaRegra(canal, opcao);
    opcao.alterado = true;
    opcao.erro = '';
  }

  precoForaDaRegra(canal: PrecosCanalAdicionalEdicao, opcao: PrecoOpcaoCanalEdicao): boolean {
    return this.podeUsarRegraDoCanal(canal) && opcao.modo === 'ESPECIFICO' &&
      !this.precoInformadoSegueRegra(canal, opcao);
  }

  podeUsarRegraDoCanal(canal: PrecosCanalAdicionalEdicao): boolean {
    return canal.canal.percentualPrecoPadrao !== 0;
  }

  precoPelaRegra(canal: PrecosCanalAdicionalEdicao, opcao: PrecoOpcaoCanalEdicao): number | null {
    const precoRegra = opcao.precoRegra == null ? null : Number(opcao.precoRegra);
    if(precoRegra != null && Number.isFinite(precoRegra)) return precoRegra;
    const precoBase = Number(opcao.precoCatalogo);
    if(opcao.precoCatalogo == null || !Number.isFinite(precoBase)) return null;
    const percentual = Number(canal.canal.percentualPrecoPadrao || 0);
    return Math.round(precoBase * (1 + percentual / 100) * 100) / 100;
  }

  ajustePercentual(canal: PrecosCanalAdicionalEdicao): string {
    const percentual = Number(canal.canal.percentualPrecoPadrao || 0);
    return percentual >= 0 ? `+ ${percentual}%` : `- ${Math.abs(percentual)}%`;
  }

  private precoInformadoSegueRegra(canal: PrecosCanalAdicionalEdicao, opcao: PrecoOpcaoCanalEdicao): boolean {
    const informado = opcao.valor == null ? null : Number(opcao.valor);
    const calculado = this.precoPelaRegra(canal, opcao);
    return informado != null && Number.isFinite(informado) && calculado != null &&
      Math.round(informado * 100) === Math.round(calculado * 100);
  }

  logoCanal(plataforma: string): string | null {
    if(plataforma === 'IFOOD') return '/assets/icons/logo-ifood.png';
    if(plataforma === 'FOOD99') return '/assets/icons/logo-99food.png';
    return null;
  }

  nomeMarketplace(plataforma: string): string {
    if(plataforma === 'IFOOD') return 'iFood';
    if(plataforma === 'FOOD99') return '99Food';
    return plataforma || 'Marketplace';
  }

  temAlteracao(canal: PrecosCanalAdicionalEdicao): boolean {
    return canal.opcoes.some(opcao => this.deveSalvarPreco(opcao));
  }

  temAlteracoes(): boolean {
    return this.canais.some(canal => canal.canal.ativo && this.temAlteracao(canal));
  }

  private deveSalvarPreco(opcao: PrecoOpcaoCanalEdicao): boolean {
    if(opcao.alterado) return true;
    return (!this.filtroOpcaoId || Number(opcao.opcaoId) === this.filtroOpcaoId) &&
      opcao.origem !== 'ESPECIFICO' && opcao.valor != null &&
      Number.isFinite(Number(opcao.valor)) && Number(opcao.valor) >= 0;
  }

  salvando(): boolean {
    return this.canais.some(canal => canal.salvando);
  }

  async salveTudo(): Promise<void> {
    if(this.salvando()) return;
    const canaisAlterados = this.canais.filter(canal => canal.canal.ativo && this.temAlteracao(canal));
    if(!canaisAlterados.length) return;
    for(const canal of canaisAlterados) for(const opcao of canal.opcoes.filter(item => this.deveSalvarPreco(item))) {
      const valor = Number(opcao.valor);
      if((!opcao.alterado || opcao.modo === 'ESPECIFICO') && (opcao.valor == null || !Number.isFinite(valor) ||
          valor < 0 || Math.round(valor * 100) !== valor * 100)) {
        opcao.erro = 'Informe um preço válido, com até duas casas decimais.';
        return;
      }
    }
    canaisAlterados.forEach(canal => canal.salvando = true);
    try {
      for(const canal of canaisAlterados) {
        for(const opcao of canal.opcoes.filter(item => this.deveSalvarPreco(item))) await this.canaisVenda.configurePrecoOpcao(
          canal.canal.id,
          opcao.opcaoId,
          opcao.alterado && opcao.modo === 'CONFIGURACAO_CANAL'
            ? {modo: 'CONFIGURACAO_CANAL'}
            : {modo: 'ESPECIFICO', valor: Number(opcao.valor), desconto: 0}
        );
      }
      await this.carregueAposSalvar();
      this.notificacao.showSuccess('Preços dos canais salvos. Nada foi publicado automaticamente.');
    } catch(erro) {
      this.erro = erro instanceof Error && erro.message
        ? erro.message : 'Não foi possível salvar os preços do canal.';
    } finally {
      canaisAlterados.forEach(canal => canal.salvando = false);
    }
  }

  private async carregueAposSalvar(): Promise<void> {
    this.carregando = false;
    await this.carregue();
  }
}
